import { useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  IntercomSettingsDocument,
  IntercomSettingsPreview,
  SettingValue,
  bulkApplyIntercomSettings,
  getIntercomSettings,
  previewIntercomSettings,
  saveIntercomSettings,
} from '../api/intercomSettings';

type Section = 'assignment' | 'calling' | 'access' | 'visitor' | 'advanced';

interface State {
  loading: boolean;
  saving: boolean;
  error: string;
  document: IntercomSettingsDocument | null;
  baseline: Record<string, SettingValue>;
  draft: Record<string, SettingValue>;
  changes: Record<string, SettingValue>;
  section: Section;
  lastPreview: IntercomSettingsPreview | null;
}

type Action =
  | { type: 'loaded'; document: IntercomSettingsDocument }
  | { type: 'failed'; error: string }
  | { type: 'set'; field: string; value: SettingValue }
  | { type: 'section'; section: Section }
  | { type: 'saving'; value: boolean }
  | { type: 'preview'; preview: IntercomSettingsPreview }
  | { type: 'discard' }
  | { type: 'committed' };

const initialState: State = {
  loading: true, saving: false, error: '', document: null,
  baseline: {}, draft: {}, changes: {}, section: 'assignment', lastPreview: null,
};

const same = (left: SettingValue | undefined, right: SettingValue | undefined): boolean => JSON.stringify(left) === JSON.stringify(right);

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'loaded': {
      const baseline = { ...(action.document.values ?? action.document.editable ?? action.document.effective.values) };
      return { ...state, loading: false, error: '', document: action.document, baseline, draft: baseline, changes: {}, lastPreview: null };
    }
    case 'failed': return { ...state, loading: false, saving: false, error: action.error };
    case 'set': {
      const draft = { ...state.draft, [action.field]: action.value };
      const changes = { ...state.changes };
      if (same(state.baseline[action.field], action.value)) delete changes[action.field];
      else changes[action.field] = action.value;
      return { ...state, draft, changes, lastPreview: null };
    }
    case 'section': return { ...state, section: action.section };
    case 'saving': return { ...state, saving: action.value, error: '' };
    case 'preview': return { ...state, lastPreview: action.preview };
    case 'discard': return { ...state, draft: { ...state.baseline }, changes: {}, lastPreview: null };
    case 'committed': return { ...state, baseline: { ...state.draft }, changes: {}, saving: false, lastPreview: null };
    default: return state;
  }
}

const idempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `settings-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function useIntercomSettingsEditor(intercomId: string) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let active = true;
    dispatch({ type: 'saving', value: false });
    getIntercomSettings(intercomId)
      .then((document) => { if (active) dispatch({ type: 'loaded', document }); })
      .catch((error) => { if (active) dispatch({ type: 'failed', error: error instanceof Error ? error.message : String(error) }); });
    return () => { active = false; };
  }, [intercomId]);

  const dirty = Object.keys(state.changes).length > 0;
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const preview = useCallback(async (scope: 'single' | 'all_property_intercoms', policy: 'fail' | 'skip_reviewed' = 'fail') => {
    if (!state.document) throw new Error('Intercom settings are still loading');
    const result = await previewIntercomSettings(intercomId, {
      expected_version: state.document.settings_version,
      changes: state.changes,
      scope,
      optional_unsupported_policy: policy,
    });
    dispatch({ type: 'preview', preview: result });
    return result;
  }, [intercomId, state.changes, state.document]);

  const saveSingle = useCallback(async (previewToken?: string) => {
    if (!state.document) throw new Error('Intercom settings are still loading');
    dispatch({ type: 'saving', value: true });
    try {
      const reviewed = previewToken ?? state.lastPreview?.preview_token ?? (await preview('single')).preview_token;
      const result = await saveIntercomSettings(intercomId, {
        expected_version: state.document.settings_version,
        idempotency_key: idempotencyKey(),
        changes: state.changes,
        preview_token: reviewed,
      });
      dispatch({ type: 'committed' });
      return result;
    } catch (error) {
      dispatch({ type: 'failed', error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }, [intercomId, preview, state.changes, state.document, state.lastPreview]);

  const saveBulk = useCallback(async (previewToken?: string) => {
    if (!state.document) throw new Error('Intercom settings are still loading');
    dispatch({ type: 'saving', value: true });
    try {
      const reviewed = previewToken ?? state.lastPreview?.preview_token ?? (await preview('all_property_intercoms')).preview_token;
      const result = await bulkApplyIntercomSettings(state.document.property_id, { idempotency_key: idempotencyKey(), preview_token: reviewed });
      dispatch({ type: 'committed' });
      return result;
    } catch (error) {
      dispatch({ type: 'failed', error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }, [preview, state.document, state.lastPreview]);

  return useMemo(() => ({
    ...state,
    dirty,
    setValue: (field: string, value: SettingValue) => dispatch({ type: 'set', field, value }),
    setSection: (section: Section) => dispatch({ type: 'section', section }),
    discard: () => dispatch({ type: 'discard' }),
    preview,
    saveSingle,
    saveBulk,
  }), [dirty, preview, saveBulk, saveSingle, state]);
}
